import { Button, Card, Form, Icon, IconSvg, Image, Size, Theme, Tray } from '@busybusy/webapp-react-ui';
import { KioskIcon, PhoneIcon, SupervisorIcon } from 'assets/icons';
import { FreeTrialGraphic } from 'assets/images';
import classNames from 'classnames';
import { HeaderDialog } from 'components';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import DualPanel from 'components/foundation/panels/DualPanel/DualPanel';
import Typography from 'components/foundation/text/Typography/Typography';
import useSlackBetaPremiumTrialAccess from 'containers/settings/integrations/vendors/slack/hooks/useSlackBetaPremiumTrialAccess';
import useSlackPremiumTrialAccess from 'containers/settings/integrations/vendors/slack/hooks/useSlackPremiumTrialAccess';
import { useToastDispatch } from 'contexts/ToastContext';
import { useOpenable } from 'hooks';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import useIsBeta from 'hooks/meta/useIsBeta';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClassName } from 'types/ClassName';
import './OwnerOnboardingTimeTrackingPage.scss';

export interface IOwnerOnboardingTimeTrackingPageProps {
  formData: ITimeTrackingOnboardingData;
  onTimeTrackingFormSubmit: (form: ITimeTrackingOnboardingData) => void;
  onPremiumTrialDialogDismissed: () => void;
  className?: ClassName;
}

export interface ITimeTrackingOnboardingData {
  onPhone: boolean;
  supervisors: boolean;
  digitalKiosk: boolean;
}

export const TimeTrackTitleRecord: Record<keyof ITimeTrackingOnboardingData, string> = {
  onPhone: 'On their phone',
  supervisors: 'Supervisors track time',
  digitalKiosk: 'Digital kiosk',
};

export interface ITimeTrackingOptions {
  key: keyof ITimeTrackingOnboardingData;
  title: string;
  body: string;
  icon: IconSvg;
}

const OwnerOnboardingTimeTrackingPage: FunctionComponent<IOwnerOnboardingTimeTrackingPageProps> = (props) => {
  const { formData, onTimeTrackingFormSubmit, onPremiumTrialDialogDismissed, className } = props;

  const [t] = useTranslation();
  const [localFormData, setLocalFormData] = useState<ITimeTrackingOnboardingData>(formData);
  const noTimeTrackingSelectedOpenable = useOpenable();
  const premiumFreeTrialOpenable = useOpenable();
  const premiumTrialAccess = useSlackPremiumTrialAccess();
  const betaPremiumTrialAccess = useSlackBetaPremiumTrialAccess();
  const toastDispatch = useToastDispatch();
  const [showErrorTip, setShowErrorTip] = useState(false);
  const isBeta = useIsBeta();
  const brand = useBrandTitle();

  const classes = classNames('owner-onboarding-time-tracking-page', className);

  const OnboardingTimeTrackingOptions: ITimeTrackingOptions[] = [
    {
      key: 'onPhone',
      title: t('On their phone'),
      body: t('Employees clock in and out from their mobile device using the {{brand}} app.', { brand }),
      icon: PhoneIcon,
    },
    {
      key: 'supervisors',
      title: t('Supervisors track time'),
      body: t(`A supervisor tracks their crew's time using the {{brand}} app.`, { brand }),
      icon: SupervisorIcon,
    },
    {
      key: 'digitalKiosk',
      title: t('Digital kiosk'),
      body: t('Each jobsite has an on-site kiosk that employees use to clock in and out.'),
      icon: KioskIcon,
    },
  ];

  function handleCardClick(key: keyof ITimeTrackingOnboardingData) {
    return () => setLocalFormData({ ...localFormData, [key]: !localFormData[key] });
  }

  const requestPremiumTrialAccess = async () => {
    let requestSuccessful = false;

    try {
      if (isBeta) {
        const response = await betaPremiumTrialAccess();
        requestSuccessful = response.status === 200;
      } else {
        const response = await premiumTrialAccess();
        requestSuccessful = response.status === 200;
      }
    } catch (e) {
      requestSuccessful = false;
    }

    if (requestSuccessful) {
      onPremiumTrialDialogDismissed();
    } else {
      toastDispatch({
        type: 'OPEN',
        payload: { label: t('Failed to send request. Try again later.'), theme: Theme.DANGER },
      });
      setShowErrorTip(true);
    }
  };

  function formRender(form: any) {
    function onFormSubmitMouseDown(e: any) {
      e.preventDefault();
    }

    return (
      <>
        <div className="card-container">
          {OnboardingTimeTrackingOptions.map((item) => {
            return (
              <Card
                key={item.key}
                type={'stroke'}
                onClick={handleCardClick(item.key)}
                className={'mb-4 ' + (localFormData[item.key] ? 'clicked' : undefined)}
              >
                <Tray spacing={3} className={'m-3'}>
                  <div className="icon-container">
                    <Icon svg={item.icon} theme={Theme.PRIMARY} size={Size.XL} />
                  </div>
                  <div>
                    <Typography tag="h2" fontSize="medium">
                      {item.title}
                    </Typography>
                    <Typography tag="h5" color="medium-gray">
                      {item.body}
                    </Typography>
                  </div>
                </Tray>
              </Card>
            );
          })}
        </div>
        <Button
          className={'form-button mb-4'}
          type={'primary'}
          onMouseDown={onFormSubmitMouseDown}
          onClick={form.handleSubmit}
        >
          {t('Finish')}
        </Button>
      </>
    );
  }

  function onFormSubmit(formData: ITimeTrackingOnboardingData) {
    onTimeTrackingFormSubmit(formData);
    premiumFreeTrialOpenable.open();
  }

  function renderForm() {
    return (
      <Form
        data={localFormData}
        onSubmit={
          !Object.values(localFormData).every((val) => val === false)
            ? onFormSubmit
            : noTimeTrackingSelectedOpenable.open
        }
        render={formRender}
        allowMultipleSubmissions={true}
      />
    );
  }

  function renderLeftContent() {
    return (
      <>
        <h2 className="py-2">{t('How would you like employees to track their time each day?')}</h2>
        <Typography tag="h5" color="medium-gray" className="py-2">
          {t('Select all that apply.')}
        </Typography>
      </>
    );
  }

  return (
    <>
      <DualPanel className={classes} leftContent={renderLeftContent} rightContent={renderForm} />
      <HeaderDialog
        title={t('Missing Selection')}
        isOpen={noTimeTrackingSelectedOpenable.isOpen}
        onClose={noTimeTrackingSelectedOpenable.close}
        divider={false}
      >
        <h4 className={'ml-6 mb-2'}>{t(`Please select at least one method for tracking time.`)}</h4>
        <Tray>
          <Button type={'secondary'} className={'m-6'} onClick={noTimeTrackingSelectedOpenable.close}>
            {t('Close')}
          </Button>
        </Tray>
      </HeaderDialog>
      <HeaderDialog
        title={t('')}
        isOpen={premiumFreeTrialOpenable.isOpen}
        onClose={premiumFreeTrialOpenable.close}
        divider={false}
      >
        <div className="premium-trial-dialog">
          <Image src={FreeTrialGraphic} width={'220px'} />
          <h1 className="fw-semi-bold mb-6">{t('14 Day Premium Free Trial!')}</h1>
          <h4 className="fc-2 mx-10 mb-6">
            {t(
              'Based on your answers, we think our Premium subscription is exactly what you are looking for. Let us prove it to you by offering a 14-day free trial of all our Pro and Premium features.'
            )}
          </h4>
          <div className="mb-4">
            <Button
              type="primary"
              onClick={() => {
                requestPremiumTrialAccess();
              }}
            >
              {t('Start Premium Free Trial')}
            </Button>
          </div>
          <div className="mb-8">
            <Button type="secondary" className="mb-4" onClick={onPremiumTrialDialogDismissed}>
              {t('No Thanks')}
            </Button>
          </div>
          <ConditionalContainer condition={showErrorTip}>
            <br />
            {t('* If you have an ad blocker, disable it and try again.')}
          </ConditionalContainer>
        </div>
      </HeaderDialog>
    </>
  );
};
export default OwnerOnboardingTimeTrackingPage;
